import {
  ClickAwayListener,
  Grow,
  Paper,
  Popper,
  MenuItem,
  MenuList,
} from '@mui/material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './Layout.css';
import { useRef, useState, useEffect } from 'react';
import Logo from '../assets/logo.svg';
import AvatarIcon from '../assets/avatar.svg';
import Menu from './Menu';
import PlusIcon from './PlusIcon';
import Footer from './Footer';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { isAuthenticated, user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
  };

  function handleListKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  }

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current?.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const isAuthPage = [
    '/login',
    '/register',
    '/forgot-password',
    '/reset-password',
  ].includes(location.pathname);

  const isLandingPage = location.pathname === '/';

  const showFooter =
    !isAuthPage && !['/welcome', '/properties/new'].includes(location.pathname);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Close mobile menu on route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <div className={`layout ${isLandingPage ? 'layout-landing' : ''}`}>
      {/* Navigation Header */}
      {!isLandingPage && (
        <header className="layout-header">
          <nav className="layout-nav">
            <div className="nav-brand">
              <Link
                to="/"
                className="brand-link"
                onClick={() => setMobileMenuOpen(false)}
              >
                <img src={Logo} alt="Valuera" className="brand-logo" />
              </Link>
            </div>

            {/* Desktop Navigation Links */}
            <div className="nav-links desktop-nav">
              {isAuthenticated ? (
                // Authenticated user navigation
                <>
                  <Menu />
                  {user?.role === 'admin' && (
                    <Link to="/admin" className="nav-link">
                      Admin
                    </Link>
                  )}
                  <div>
                    <button
                      ref={anchorRef}
                      id="composition-button"
                      className="profile-menu-btn"
                      aria-controls={open ? 'composition-menu' : undefined}
                      aria-expanded={open ? 'true' : undefined}
                      aria-haspopup="true"
                      onClick={handleToggle}
                      aria-label="Profile"
                    >
                      <img
                        src={AvatarIcon}
                        alt="Profile"
                        className="profile-menu-icon"
                      />
                    </button>
                    <Popper
                      open={open}
                      anchorEl={anchorRef.current}
                      role={undefined}
                      placement="bottom-start"
                      transition
                      disablePortal
                    >
                      {({ TransitionProps, placement }) => (
                        <Grow
                          {...TransitionProps}
                          style={{
                            transformOrigin:
                              placement === 'bottom-start'
                                ? 'left top'
                                : 'left bottom',
                          }}
                        >
                          <Paper className="profile-dropdown-paper">
                            <ClickAwayListener onClickAway={handleClose}>
                              <MenuList
                                autoFocusItem={open}
                                id="composition-menu"
                                aria-labelledby="composition-button"
                                onKeyDown={handleListKeyDown}
                              >
                                <MenuItem
                                  onClick={handleClose}
                                  component={Link}
                                  to="/profile"
                                >
                                  Profile
                                </MenuItem>
                                <MenuItem
                                  onClick={handleClose}
                                  component={Link}
                                  to="/shop"
                                >
                                  Subscription
                                </MenuItem>
                                <MenuItem
                                  onClick={(e) => {
                                    handleClose(e);
                                    logout();
                                  }}
                                  data-testid="logout-button"
                                >
                                  Logout
                                </MenuItem>
                              </MenuList>
                            </ClickAwayListener>
                          </Paper>
                        </Grow>
                      )}
                    </Popper>
                  </div>
                  <button
                    className="new-property-btn"
                    onClick={() => navigate('/properties/new')}
                  >
                    <PlusIcon className="new-property-icon" />
                    <span className="new-property-text">
                      Neue Immobilie hinzufügen
                    </span>
                  </button>
                </>
              ) : (
                // Unauthenticated user navigation
                !isAuthPage && (
                  <>
                    <Link to="/pricing" className="nav-link">
                      Kosten
                    </Link>
                    <Link to="/login" className="nav-link">
                      Login
                    </Link>
                    <Link to="/register" className="nav-link">
                      Register
                    </Link>
                  </>
                )
              )}
            </div>

            {/* Mobile Navigation Toggle Button */}
            {!isAuthPage && (
              <div className="mobile-nav-toggle-wrapper">
                {isAuthenticated && (
                  <button
                    className="mobile-quick-add-btn"
                    onClick={() => navigate('/properties/new')}
                    aria-label="Neue Immobilie hinzufügen"
                  >
                    <PlusIcon className="new-property-icon" />
                  </button>
                )}
                <button
                  className="mobile-hamburger-btn"
                  onClick={() => setMobileMenuOpen((prev) => !prev)}
                  aria-label="Navigation öffnen"
                  aria-expanded={mobileMenuOpen}
                >
                  <span
                    className={`hamburger-bar ${mobileMenuOpen ? 'open' : ''}`}
                  />
                  <span
                    className={`hamburger-bar ${mobileMenuOpen ? 'open' : ''}`}
                  />
                  <span
                    className={`hamburger-bar ${mobileMenuOpen ? 'open' : ''}`}
                  />
                </button>
              </div>
            )}
          </nav>

          {/* Mobile Drawer Menu */}
          {mobileMenuOpen && !isAuthPage && (
            <div
              className="mobile-drawer-overlay"
              onClick={() => setMobileMenuOpen(false)}
            >
              <div
                className="mobile-drawer-content"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="mobile-drawer-header">
                  <span className="mobile-drawer-title">Navigation</span>
                  <button
                    className="mobile-drawer-close"
                    onClick={() => setMobileMenuOpen(false)}
                    aria-label="Schließen"
                  >
                    ✕
                  </button>
                </div>
                <div className="mobile-drawer-links">
                  {isAuthenticated ? (
                    <>
                      <Link
                        to="/dashboard"
                        className="mobile-nav-link"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Portfolioübersicht
                      </Link>
                      <Link
                        to="/properties"
                        className="mobile-nav-link"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Meine Immobilien
                      </Link>
                      {user?.role === 'admin' && (
                        <Link
                          to="/admin"
                          className="mobile-nav-link"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          Admin
                        </Link>
                      )}
                      <div className="mobile-drawer-divider" />
                      <Link
                        to="/profile"
                        className="mobile-nav-link"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Profil
                      </Link>
                      <Link
                        to="/shop"
                        className="mobile-nav-link"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Abonnement
                      </Link>
                      <div className="mobile-drawer-divider" />
                      <button
                        className="mobile-drawer-add-btn"
                        onClick={() => {
                          setMobileMenuOpen(false);
                          navigate('/properties/new');
                        }}
                      >
                        <PlusIcon className="new-property-icon" />
                        <span>Neue Immobilie hinzufügen</span>
                      </button>
                      <button
                        className="mobile-drawer-logout-btn"
                        onClick={() => {
                          setMobileMenuOpen(false);
                          logout();
                        }}
                      >
                        Abmelden
                      </button>
                    </>
                  ) : (
                    <>
                      <Link
                        to="/pricing"
                        className="mobile-nav-link"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Kosten
                      </Link>
                      <Link
                        to="/login"
                        className="mobile-nav-link"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Login
                      </Link>
                      <Link
                        to="/register"
                        className="mobile-nav-link"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Register
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </header>
      )}

      {/* Main Content */}
      <main
        className={`layout-main ${isLandingPage ? 'layout-main-landing' : ''}`}
      >
        {children}
      </main>

      {showFooter && <Footer />}
    </div>
  );
};

export default Layout;
